import { Component, ReactNode } from "react"
import { connect } from "react-redux"
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router"

import client from "client/services/actions"
import {
  Admin,
  Call,
  Diagnostic,
  Finale,
  Home,
  InsertRelative,
  Loading,
  Lobby,
  Login,
  NirAndExemption,
  NotAuthorized,
  Payment,
  Profile,
  Signup,
  Statistics,
  Survey,
  VerifyPhone,
} from "containers"
import Lenses from "containers/Lenses/Lenses"
import { AdminCarer } from "containers/Carer/Carer"
import Consent from "containers/Consent/Consent"
import Demonstration from "containers/Demonstration/Demonstration"
import Doctors from "containers/Doctors/Doctors"
import NoNetwork from "containers/Errors/NoNetwork/NoNetwork"
import CompletePatientInfo from "containers/CompletePatientInfo/CompletePatientInfo"
import ModifyRelative from "containers/Profile/ModifyRelative"
import ResetPassword from "containers/ResetPassword/ResetPassword"
import Timeline from "containers/Timeline/Timeline"
import { CallState } from "types/payload"
import { Dispatcher } from "types/redux"
import { ClientStore, ConfigurationStore } from "types/store"

import DocumentNotFound from "containers/Errors/DocumentNotFound/DocumentNotFound"
import ReadersConfiguration from "containers/ContactlessPayment/ReadersConfiguration"

import {
  ADMIN_CARERS_PAGE,
  ADMIN_PAGE,
  CALL_PAGE,
  CONSENT_PAGE,
  DIAGNOSTIC,
  FINALE_PAGE,
  GET_DOCTOR_PAGE,
  HOME_PAGE,
  INSERT_RELATIVE_PAGE,
  LOBBY_PAGE,
  LOGIN_PAGE,
  MAINTENANCE_PAGE,
  MODIFY_RELATIVE_PAGE,
  NIR_AND_EXEMPTION_PAGE,
  NO_NETWORK_PAGE,
  NOT_AUTHORIZED_PAGE,
  PAYMENT_PAGE,
  PROFILE_PAGE,
  REGISTER_PAGE,
  RESET_PASSWORD_PAGE,
  SET_RELATIVE_PAGE,
  COMPLETE_PATIENT_INFO_PAGE,
  STATISTICS_PAGE,
  SURVEY_PAGE,
  TIMELINE_PAGE,
  TUTORIAL_OPTIC_PAGE,
  VERIFY_PHONE_PAGE,
  DOCUMENT_NOT_FOUND_PAGE,
  LENS_PAGE,
  READERS_CONFIGURATION_PAGE,
} from "./constants"
import {
  GUARD_IS_ADMIN,
  GUARD_IS_LOGGED_IN,
  GUARD_IS_NOT_ADMIN,
  GUARD_IS_NOT_LOGGED_IN,
  GUARD_IS_NOT_VERIFIED,
  GUARD_IS_VERIFIED,
} from "./guards"
import Maintenance from "containers/Maintenance/Maintenance"

interface RootProps {
  fetchInstallation: () => void
  success: boolean
  admin: boolean
  login: boolean
  loading: boolean
  verified: boolean
  call: boolean
  maintenance?: boolean
}

interface ProtectedRouteProps {
  redirectTo: string | null
  children?: ReactNode
}

const ProtectedRoute = ({ redirectTo, children }: ProtectedRouteProps) => {
  const location = useLocation()
  if (redirectTo && redirectTo !== location.pathname)
    return <Navigate to={redirectTo} replace />
  return children ? <>{children}</> : <Outlet />
}

class Root extends Component<RootProps> {
  state = { shouldRefresh: false }

  constructor(props: RootProps) {
    super(props)
    this.props.fetchInstallation()
  }

  private guardRedirect = (guardValue: string): string | null => {
    const { admin, login, verified, call } = this.props
    if (call) return CALL_PAGE
    switch (guardValue) {
      case GUARD_IS_ADMIN:
        return admin ? null : ADMIN_PAGE
      case GUARD_IS_NOT_ADMIN:
        return !admin ? null : HOME_PAGE
      case GUARD_IS_LOGGED_IN:
        if (!admin) return ADMIN_PAGE
        if (!login) return LOBBY_PAGE
        return null
      case GUARD_IS_VERIFIED:
        if (!admin) return ADMIN_PAGE
        if (!login) return LOBBY_PAGE
        if (!verified) return VERIFY_PHONE_PAGE
        return null
      case GUARD_IS_NOT_VERIFIED:
        if (!admin) return ADMIN_PAGE
        if (!login) return LOBBY_PAGE
        if (verified) return PROFILE_PAGE
        return null
      case GUARD_IS_NOT_LOGGED_IN:
        if (!admin) return ADMIN_PAGE
        if (login) return PROFILE_PAGE
        return null
      default:
        return null
    }
  }

  /**
   *
   * TODO
   * replace <Route path="*" element={<Home />} />
   * by  <Route path="*" element={<NotFound />} />
   * after migration from pharma to kiosk
   */
  public render = (): ReactNode => {

    if (this.props.maintenance || /^\/maintenance/.test(window.location.pathname)) {
      return <Maintenance />
    }

    if (window.location.pathname === NO_NETWORK_PAGE) return <NoNetwork />

    if (this.props.loading) return <Loading />

    if (!this.props.success) {
      return <NotAuthorized />
    }

    return (
      <Routes>
        <Route path={MAINTENANCE_PAGE} element={<Maintenance />} />
        <Route path={NO_NETWORK_PAGE} element={<NoNetwork />} />
        <Route path={NOT_AUTHORIZED_PAGE} element={<NotAuthorized />} />
        <Route
          element={
            <ProtectedRoute
              redirectTo={this.guardRedirect(GUARD_IS_NOT_ADMIN)}
            />
          }
        >
          <Route path="/" element={<Home />} />
          <Route path={ADMIN_PAGE} element={<Admin />} />
        </Route>
        <Route
          element={
            <ProtectedRoute redirectTo={this.guardRedirect(GUARD_IS_ADMIN)} />
          }
        >
          <Route path={HOME_PAGE} element={<Home />} />
          <Route path={STATISTICS_PAGE} element={<Statistics />} />
          <Route path={NIR_AND_EXEMPTION_PAGE} element={<NirAndExemption />} />
          <Route path={ADMIN_CARERS_PAGE} element={<AdminCarer />} />
          <Route path={READERS_CONFIGURATION_PAGE} element={<ReadersConfiguration />} />
        </Route>
        <Route
          element={
            <ProtectedRoute
              redirectTo={this.guardRedirect(GUARD_IS_VERIFIED)}
            />
          }
        >
          <Route path={DIAGNOSTIC} element={<Diagnostic />} />
          <Route path={SURVEY_PAGE} element={<Survey />} />
          <Route path={FINALE_PAGE} element={<Finale />} />
          <Route path={PROFILE_PAGE} element={<Profile />} />
          <Route path={CALL_PAGE} element={<Call />} />
          <Route path={INSERT_RELATIVE_PAGE} element={<InsertRelative />} />
          <Route path={PAYMENT_PAGE} element={<Payment />} />
          <Route path={SET_RELATIVE_PAGE} element={<CompletePatientInfo />} />
          <Route path={COMPLETE_PATIENT_INFO_PAGE} element={<CompletePatientInfo />} />
          <Route path={CONSENT_PAGE} element={<Consent />} />
          <Route path={`${TIMELINE_PAGE}`} element={<Timeline />} />
          <Route path={TUTORIAL_OPTIC_PAGE} element={<Demonstration />} />
        </Route>
        <Route
          element={
            <ProtectedRoute
              redirectTo={this.guardRedirect(GUARD_IS_NOT_VERIFIED)}
            />
          }
        >
          <Route path={VERIFY_PHONE_PAGE} element={<VerifyPhone />} />
        </Route>
        <Route
          element={
            <ProtectedRoute
              redirectTo={this.guardRedirect(GUARD_IS_LOGGED_IN)}
            />
          }
        >
          <Route path={GET_DOCTOR_PAGE} element={<Doctors />} />
          <Route
            path={`${MODIFY_RELATIVE_PAGE}/:id`}
            element={<ModifyRelative />}
          />
        </Route>
        <Route
          element={
            <ProtectedRoute
              redirectTo={this.guardRedirect(GUARD_IS_NOT_LOGGED_IN)}
            />
          }
        >
          <Route path={REGISTER_PAGE} element={<Signup />} />
          <Route path={LOGIN_PAGE} element={<Login />} />
          <Route path={LOBBY_PAGE} element={<Lobby />} />
          <Route
            path={RESET_PASSWORD_PAGE}
            element={
              <ResetPassword
                history={{
                  push: function (s: string): void {
                    throw new Error("Function not implemented.")
                  },
                }}
              />
            }
          />
        </Route>
        <Route path={DOCUMENT_NOT_FOUND_PAGE} element={<DocumentNotFound />} />
        <Route path={LENS_PAGE} element={<Lenses />} />
        <Route path="*" element={<Home />} />
      </Routes>
    )
  }
}

export const mapStateToProps = ({
  client,
  configuration,
}: {
  client: ClientStore,
  configuration: ConfigurationStore
}): RootProps => {
  return {
    success: client.success,
    loading: client.loading,
    admin: !!client.admin,
    login: !!client.customer,
    call: !!client.call && client.call.state !== CallState.ENDED,
    verified: client.customer?.is_verified ? true : false,
    maintenance: configuration?.configuration?.maintenance?.active,
  } as RootProps
}

const mapDispatchToProps = (dispatch: Dispatcher): Partial<RootProps> => {
  return {
    fetchInstallation: () => dispatch(client.installation),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Root as any)
